import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { generateMockFlights } from '../utils/mockData';
import { Flight } from '../types';
import styles from './Search.module.css';

export default function Search() {
  const [flights, setFlights] = useState<Flight[]>([]);
  const [loading, setLoading] = useState(true);
  const { state, dispatch } = useBooking();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect if no search criteria
    if (!state.searchCriteria) {
      navigate('/');
      return;
    }

    // Simulate loading delay for better UX
    const timer = setTimeout(() => {
      const mockFlights = generateMockFlights(
        state.searchCriteria!.from,
        state.searchCriteria!.to,
        state.searchCriteria!.departure
      );
      setFlights(mockFlights);
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timer);
  }, [state.searchCriteria, navigate]);

  const handleFlightSelect = (flight: Flight) => {
    dispatch({ type: 'SET_SELECTED_FLIGHT', payload: flight });
    navigate('/checkout');
  };

  const handleModifySearch = () => {
    navigate('/');
  };

  if (!state.searchCriteria) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.searchBar}>
          <div className={styles.searchInfo}>
            <div className={styles.searchItem}>
              <span className={styles.searchLabel}>From</span>
              <span className={styles.searchValue}>{state.searchCriteria.from}</span>
            </div>
            <div className={styles.searchItem}>
              <span className={styles.searchLabel}>To</span>
              <span className={styles.searchValue}>{state.searchCriteria.to}</span>
            </div>
            <div className={styles.searchItem}>
              <span className={styles.searchLabel}>Departure</span>
              <span className={styles.searchValue}>{state.searchCriteria.departure}</span>
            </div>
            {state.searchCriteria.return && (
              <div className={styles.searchItem}>
                <span className={styles.searchLabel}>Return</span>
                <span className={styles.searchValue}>{state.searchCriteria.return}</span>
              </div>
            )}
          </div>
          <button
            className={styles.modifyButton}
            onClick={handleModifySearch}
          >
            Modify Search
          </button>
        </div>
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>
            Finding the best flights for you...
          </div>
        ) : (
          <>
            <div className={styles.resultsHeader}>
              <h2 className={styles.resultsTitle}>Available Flights</h2>
              <p className={styles.resultsCount}>{flights.length} flights found</p>
            </div>

            <div className={styles.flightList}>
              {flights.map((flight) => (
                <div
                  key={flight.id}
                  className={styles.flightCard}
                  onClick={() => handleFlightSelect(flight)}
                >
                  <div className={styles.flightInfo}>
                    <div className={styles.flightRoute}>
                      <div className={styles.airport}>
                        <div className={styles.airportCode}>{flight.departure.airport}</div>
                        <div className={styles.airportTime}>{flight.departure.time}</div>
                      </div>
                      <div className={styles.airport}>
                        <div className={styles.airportCode}>{flight.arrival.airport}</div>
                        <div className={styles.airportTime}>{flight.arrival.time}</div>
                      </div>
                    </div>

                    <div className={styles.flightDetails}>
                      <div className={styles.airline}>{flight.airline}</div>
                      <div className={styles.flightNumber}>{flight.flightNumber}</div>
                      <div className={styles.duration}>{flight.duration}</div>
                    </div>

                    <div className={styles.flightPrice}>
                      <div className={styles.price}>${flight.price}</div>
                      <div className={styles.priceLabel}>per person</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}